import { useCallback, useState } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { IconUpload, IconPhoto, IconAlertCircle } from '@tabler/icons-react';
import { uploadAdImage } from '../services/storageService';
import type { AdLevel } from '../types';

const MAX_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB
const ACCEPTED_TYPES = { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] };

interface Props {
  campaignId: string;
  level: AdLevel;
  currentImageUrl?: string;
  onUploaded: (url: string) => void;
}

export default function AdImageDropzone({ campaignId, level, currentImageUrl, onUploaded }: Props) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | undefined>(currentImageUrl);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        const msg = rejectedFiles[0].errors[0]?.message ?? 'Invalid file';
        setError(msg);
        return;
      }

      const file = acceptedFiles[0];
      if (!file) return;

      if (file.size > MAX_SIZE_BYTES) {
        setError(
          `File too large. Maximum size is 1 MB (current: ${Math.round(file.size / 1024)} KB)`
        );
        return;
      }

      // Show local preview immediately
      const localUrl = URL.createObjectURL(file);
      setPreview(localUrl);

      setUploading(true);
      setProgress(30);

      try {
        setProgress(60);
        const downloadUrl = await uploadAdImage(campaignId, level, file);
        setProgress(100);
        onUploaded(downloadUrl);
        setPreview(downloadUrl);
      } catch (err) {
        setError('Upload failed. Please try again.');
        setPreview(currentImageUrl);
        console.error('AdImageDropzone upload error:', err);
      } finally {
        setUploading(false);
        setTimeout(() => setProgress(0), 800);
      }
    },
    [campaignId, level, currentImageUrl, onUploaded]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxFiles: 1,
    maxSize: MAX_SIZE_BYTES,
    disabled: uploading,
  });

  return (
    <div>
      {/* Drop zone */}
      <div
        {...getRootProps()}
        className={`card p-3 text-center cursor-pointer ${
          isDragActive ? 'border-primary bg-primary-lt' : 'border-dashed'
        }`}
        style={{
          border: '2px dashed var(--tblr-border-color)',
          borderRadius: 8,
          transition: 'all 0.15s ease',
          cursor: uploading ? 'not-allowed' : 'pointer',
        }}
      >
        <input {...getInputProps()} />

        {preview ? (
          <div>
            <img
              src={preview}
              alt={`Level ${level} ad preview`}
              style={{ width: '100%', height: 60, objectFit: 'cover', borderRadius: 4 }}
            />
            <div className="text-muted small mt-1">
              {isDragActive ? 'Drop to replace' : 'Click or drag to replace'}
            </div>
          </div>
        ) : (
          <div className="py-2">
            {isDragActive ? (
              <IconUpload className="icon text-primary mb-1" size={24} />
            ) : (
              <IconPhoto className="icon text-muted mb-1" size={24} />
            )}
            <div className="text-muted small">
              {isDragActive ? 'Drop image here' : 'Click or drag image here'}
              <br />
              <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                JPEG or PNG · 1200×300px recommended · max 1 MB
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Upload progress */}
      {uploading && (
        <div className="progress mt-1" style={{ height: 4 }}>
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${progress}%`, transition: 'width 0.3s ease' }}
          />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-danger small mt-1 d-flex align-items-center gap-1">
          <IconAlertCircle size={14} />
          {error}
        </div>
      )}
    </div>
  );
}
